import { z } from "zod";
import { inferProductCategoryName } from "./category-names.js";
export const categoryUpdateSchema = z.object({
    name: z.string().trim().min(1, "分类名称不能为空").max(80, "分类名称过长").optional(),
    sortOrder: z.number().int().min(0).max(99_999).optional(),
    active: z.boolean().optional(),
}).refine((input) => Object.keys(input).length > 0, "没有需要更新的内容");
/** A category is a full, even-length prefix. The final pair identifies the product. */
export const categoryPrefixSchema = z.string().regex(/^(?:[0-9]{2}){1,24}$/, "分类编码应为每两位一层的数字前缀");
export const codeCategoryUpdateSchema = z.object({
    name: z.string().trim().min(1, "分类名称不能为空").max(80, "分类名称过长"),
    sortOrder: z.number().int().min(0).max(99_999).default(0),
});
export function getProductCategoryPrefixes(code) {
    if (!/^[0-9]{4,50}$/.test(code) || code.length % 2 !== 0)
        return [];
    const prefixes = [];
    for (let end = 2; end < code.length; end += 2)
        prefixes.push(code.slice(0, end));
    return prefixes;
}
export function matchesCategoryPrefix(code, prefix) {
    return getProductCategoryPrefixes(code).includes(prefix);
}
/** Build from the current products so creates, code edits and imports appear immediately. */
export function buildCodeCategories(products) {
    const nodes = new Map();
    const seenProducts = new Set();
    const rootNames = new Map();
    const productNames = new Map();
    for (const product of products) {
        if (seenProducts.has(product.id))
            continue;
        seenProducts.add(product.id);
        const prefixes = getProductCategoryPrefixes(product.code);
        const rootPrefix = prefixes[0];
        if (rootPrefix && product.categoryName) {
            const names = rootNames.get(rootPrefix) ?? new Map();
            names.set(product.categoryName, (names.get(product.categoryName) ?? 0) + 1);
            rootNames.set(rootPrefix, names);
        }
        for (const prefix of prefixes) {
            const names = productNames.get(prefix) ?? [];
            names.push(product.name ?? "");
            productNames.set(prefix, names);
            let node = nodes.get(prefix);
            if (!node) {
                node = {
                    prefix,
                    parentPrefix: prefix.length === 2 ? null : prefix.slice(0, -2),
                    segment: prefix.slice(-2),
                    depth: prefix.length / 2,
                    label: `编码 ${prefix.slice(-2)}`,
                    path: prefix.match(/.{2}/g).join(" / "),
                    productCount: 0,
                    activeProductCount: 0,
                    skuCount: 0,
                };
                nodes.set(prefix, node);
            }
            node.productCount += 1;
            node.activeProductCount += product.active === false ? 0 : 1;
            node.skuCount += product.skuCount ?? 0;
        }
    }
    for (const [prefix, names] of rootNames) {
        // Existing root category names remain authoritative before automatic inference.
        const name = [...names].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0], "zh-CN"))[0]?.[0];
        if (name) {
            nodes.get(prefix).name = name;
            nodes.get(prefix).label = `${prefix} · ${name}`;
        }
    }
    for (const node of nodes.values()) {
        if (node.name)
            continue;
        const name = inferProductCategoryName(productNames.get(node.prefix) ?? []);
        if (name) {
            node.name = name;
            node.label = `${node.prefix} · ${name}`;
        }
    }
    return [...nodes.values()].sort((a, b) => a.prefix.localeCompare(b.prefix, "en"));
}
